import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, Mail, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmailStatCard } from "./emailOps/EmailStatCard";
import { EmailFilters } from "./emailOps/EmailFilters";
import { EmailLogTable } from "./emailOps/EmailLogTable";
import {
  type Counts,
  type LogRow,
  type RangePreset,
  type StatusFilter,
  PAGE_SIZE,
} from "./emailOps/types";

export function EmailDeliverabilityPanel() {
  const [range, setRange] = useState<RangePreset>("7d");
  const [customStart, setCustomStart] = useState<string>("");
  const [customEnd, setCustomEnd] = useState<string>("");
  const [template, setTemplate] = useState<string>("all");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [page, setPage] = useState(0);

  const [templates, setTemplates] = useState<string[]>([]);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [rows, setRows] = useState<LogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const windowParams = useMemo(() => {
    if (range === "custom" && customStart && customEnd) {
      return {
        start: new Date(customStart).toISOString(),
        end: new Date(customEnd).toISOString(),
      };
    }
    return { range: range === "custom" ? "7d" : range };
  }, [range, customStart, customEnd]);

  const callOps = useCallback(async (payload: Record<string, unknown>) => {
    const { data, error } = await supabase.functions.invoke("admin-email-ops", {
      body: payload,
    });
    if (error) throw new Error(error.message);
    if (data && typeof data === "object" && "error" in data && (data as { error?: string }).error) {
      throw new Error((data as { error: string }).error);
    }
    return data as Record<string, unknown>;
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const tplArg = template === "all" ? null : template;
      const statusArg = status === "all" ? null : status;
      const [statsRes, logRes] = await Promise.all([
        callOps({ action: "email_stats", template: tplArg, ...windowParams }),
        callOps({
          action: "email_log",
          template: tplArg,
          status: statusArg,
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
          ...windowParams,
        }),
      ]);
      setCounts(statsRes.counts as Counts);
      setRows((logRes.rows as LogRow[]) ?? []);
      setTotal((logRes.total as number) ?? 0);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to load email data";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [callOps, template, status, page, windowParams]);

  useEffect(() => {
    let cancelled = false;
    callOps({ action: "email_templates" })
      .then((res) => {
        if (cancelled) return;
        setTemplates(((res.templates as string[]) ?? []));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [callOps]);

  useEffect(() => {
    setPage(0);
  }, [range, customStart, customEnd, template, status]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <Card className="shadow-soft">
      <CardHeader className="gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            <CardTitle>Email deliverability</CardTitle>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void refresh()}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        <EmailFilters
          range={range}
          setRange={setRange}
          customStart={customStart}
          setCustomStart={setCustomStart}
          customEnd={customEnd}
          setCustomEnd={setCustomEnd}
          template={template}
          setTemplate={setTemplate}
          status={status}
          setStatus={setStatus}
          templates={templates}
        />
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <EmailStatCard label="Total emails" value={counts?.total ?? null} loading={loading} />
          <EmailStatCard label="Sent" value={counts?.sent ?? null} loading={loading} tone="success" />
          <EmailStatCard label="Failed" value={counts?.failed ?? null} loading={loading} tone="danger" />
          <EmailStatCard label="Suppressed" value={counts?.suppressed ?? null} loading={loading} tone="warning" />
        </div>

        {error ? (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4" />
            <div>{error}</div>
          </div>
        ) : null}

        <EmailLogTable rows={rows} loading={loading} total={total} page={page} setPage={setPage} />
      </CardContent>
    </Card>
  );
}
